import { observer } from 'mobx-react';
import React, { useContext, useEffect } from 'react';
import { Event } from '../../../config/Registration';
import { RootStoreContext } from '../../../stores/RootStore';
import './ClassesScheduleEvent.css';
import { eventDateKey, eventPriceLabel, eventTimeLabel } from './classesSchedule';
import { getContrastingTextColor, getVenueColor } from '../../../util/venueColors';

const DEFAULT_EVENT_IMAGE = `${process.env.PUBLIC_URL || ''}/01.png`;

interface ClassesScheduleEventProps {
  event: Event;
  selectedDate: Date;
  onSelect: (event: Event) => void;
}

const ClassesScheduleEvent = observer(({ event, selectedDate, onSelect }: ClassesScheduleEventProps) => {
  const { eventPhotoStore } = useContext(RootStoreContext);
  const selectedKey = `${selectedDate.getFullYear()}-${String(selectedDate.getMonth() + 1).padStart(2, '0')}-${String(selectedDate.getDate()).padStart(2, '0')}`;
  const programs = (event.programTimes || []).filter(program => eventDateKey(program.startTime) === selectedKey);
  const visiblePrograms = programs.length ? programs : [{
    startTime: event.startTime,
    endTime: event.endTime,
    programName: 'Event program'
  }];
  const eventPhoto = eventPhotoStore.getEventPhoto(event.eventId);
  const isBooked = (event.purchasedTickets || []).length > 0;
  const venueColor = getVenueColor(event.venueId);

  useEffect(() => {
    if (!eventPhoto) eventPhotoStore.loadEventPhotoQuietly(event.eventId);
  }, [event.eventId, eventPhoto, eventPhotoStore]);

  return (
    <button
      type="button"
      className={`classes-event-card ${isBooked ? 'booked' : ''}`}
      onClick={() => onSelect(event)}
      aria-label={`Open event ${event.title}${isBooked ? ', booked' : ''}${event.recommendedForUser ? ', recommended for you' : ''}`}
    >
      <span className="classes-event-content">
        <span className="classes-event-badges">
          <span
            className="venue-badge classes-event-venue"
            style={{ backgroundColor: venueColor, color: getContrastingTextColor(venueColor) }}
          >
            {event.venueName}
          </span>
          {event.recommendedForUser && (
            <span className="classes-event-recommended">Recommended for you</span>
          )}
        </span>
        <span className="classes-event-title">{event.title}</span>
        <span className="classes-event-body">
          <img
            className="classes-event-thumbnail"
            src={eventPhoto?.photoUrl || DEFAULT_EVENT_IMAGE}
            alt=""
            onError={event => {
              event.currentTarget.onerror = null;
              event.currentTarget.src = DEFAULT_EVENT_IMAGE;
            }}
          />
          <span className="classes-event-programs">
            {visiblePrograms.map(program => (
              <span className="classes-event-program" key={`${program.startTime}-${program.programName}`}>
                <span className="classes-event-time">{eventTimeLabel(program.startTime)}</span>
                {program.programName}
              </span>
            ))}
          </span>
          <span className="classes-event-chevron" aria-hidden="true">›</span>
        </span>
        <span className="classes-event-price">{eventPriceLabel(event)}</span>
      </span>
    </button>
  );
});

export default ClassesScheduleEvent;
